import React, { useState } from 'react'
import {
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core'
import {
  Add as AddIcon,
  Edit as EditIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Refresh as RefreshIcon,
} from '@material-ui/icons'
import moment from 'moment'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'

import {
  useQueryPaginator,
  DetailTable,
  HoursOfOperationField,
  NumberField,
} from 'src/components/Table'
import { FacilityDetailTable } from 'src/components/Table/detail/Facility'
import { AppointmentDialog } from 'src/modules/supply_chain/components/AppointmentDialog'
import { FacilityDialog } from 'src/modules/supply_chain/components/FacilityDialog'
import { FacilityScheduleDialog } from 'src/modules/supply_chain/components/FacilityScheduleDialog'
import { ExportDialog } from 'src/components/Dialog'
import { HierarchyDetail } from 'src/modules/supply_chain/components/HierarchyDetail'
import { ContainerListTable } from 'src/components/Table/list/Container'
import { DamagedListTable } from 'src/components/Table/list/Damaged'
import { DockListTable } from 'src/components/Table/list/Dock'
import { EventListTable } from 'src/components/Table/list/Event'
import { SkuListTable } from 'src/components/Table/list/Sku'
import { UnitListTable } from 'src/components/Table/list/Unit'
import { Appointment, Facility } from 'src/api/models'
import { Calendar, CalendarView } from 'src/components/Calendar'

import toast from 'react-hot-toast'
import { Notification } from 'src/components/Notification'
import { skuit } from 'src/services/skuit'
import { useQuery, UseQueryResult } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}

interface FacilityProps {
  facility: UseQueryResult<Facility>
}

const ConfigTab = (props: FacilityProps) => {
  const { facility } = props
  const [fsOpen, setFSOpen] = useState(false)
  const dockPaginator = useQueryPaginator('dock-list', skuit.sc.docks.list, {
    params: { page_size: 5, facilities: facility.data?.guid },
  })
  const schedule = useQuery(['facility-schedule', facility.data?.guid], () =>
    skuit.sc.facilities.schedule.list(facility.data?.guid || '')
  )

  return (
    <>
      {fsOpen && (
        <FacilityScheduleDialog
          open={fsOpen}
          handleClose={() => setFSOpen(false)}
          facility={facility.data}
          schedule={schedule.data}
          onSave={() => schedule.refetch()}
        />
      )}
      <DetailTab>
        <DockListTable
          facility={facility.data}
          paginator={dockPaginator}
          fields={['name', 'dockType']}
        />
        <Card>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  Scheduling Configuration
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={() => setFSOpen(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
            <DetailTable
              obj={schedule}
              fields={[
                { id: 'maxInbound', label: 'Max Inbound Appointments', renderer: NumberField },
                { id: 'maxOutbound', label: 'Max Outbound Appointments', renderer: NumberField },
                {
                  id: 'hoursOfOperation.sun',
                  label: 'Sunday Hours',
                  renderer: HoursOfOperationField(schedule.data?.hoursOfOperation?.default),
                },
                {
                  id: 'hoursOfOperation.mon',
                  label: 'Monday Hours',
                  renderer: HoursOfOperationField(schedule.data?.hoursOfOperation?.default),
                },
                {
                  id: 'hoursOfOperation.tue',
                  label: 'Tuesday Hours',
                  renderer: HoursOfOperationField(schedule.data?.hoursOfOperation?.default),
                },
                {
                  id: 'hoursOfOperation.wed',
                  label: 'Wednesday Hours',
                  renderer: HoursOfOperationField(schedule.data?.hoursOfOperation?.default),
                },
                {
                  id: 'hoursOfOperation.thu',
                  label: 'Thursday Hours',
                  renderer: HoursOfOperationField(schedule.data?.hoursOfOperation?.default),
                },
                {
                  id: 'hoursOfOperation.fri',
                  label: 'Friday Hours',
                  renderer: HoursOfOperationField(schedule.data?.hoursOfOperation?.default),
                },
                {
                  id: 'hoursOfOperation.sat',
                  label: 'Saturday Hours',
                  renderer: HoursOfOperationField(schedule.data?.hoursOfOperation?.default),
                },
              ]}
            />
          </CardContent>
        </Card>
      </DetailTab>
    </>
  )
}

const InventoryTab = () => {
  const { guid } = useParams<PathProps>()

  const skuPaginator = useQueryPaginator(
    'facility-sku-list',
    (config) => skuit.sc.facilities.skus.list(guid, config),
    {
      params: { page_size: 5 },
    }
  )
  const unitsPaginator = useQueryPaginator('unit-list', skuit.sc.units.list, {
    params: { page_size: 5, facilities: guid },
  })
  const containerPaginator = useQueryPaginator('container-list', skuit.sc.containers.list, {
    params: { page_size: 5, facilities: guid },
  })
  const damagedPaginator = useQueryPaginator('damaged-list', skuit.sc.damaged.list, {
    params: { page_size: 5, facilities: guid },
  })

  return (
    <DetailTab>
      <SkuListTable
        paginator={skuPaginator}
        title="SKUs in Facility"
        fields={[
          'sku',
          'product',
          'client',
          'numAvailable',
          'numReserved',
          'numAwaitingShipment',
          'isActive',
          'health',
        ]}
      />
      <ContainerListTable
        paginator={containerPaginator}
        title="Containers in Facility"
        fields={['name', 'containerType', 'path']}
      />
      <UnitListTable
        paginator={unitsPaginator}
        title="Units in Facility"
        fields={['productName', 'product', 'sku', 'client', 'numUnits', 'path']}
      />
      <DamagedListTable
        paginator={damagedPaginator}
        title="Damages in Facility"
        fields={['guid', 'client', 'product', 'sku', 'path']}
      />
    </DetailTab>
  )
}

const calculateDateRange = (view: CalendarView, date: moment.Moment) => {
  switch (view) {
    case 'day':
      return [date.clone(), date.clone()]
    case 'week':
      return [date.clone().weekday(0), date.clone().weekday(6)]
    case 'month':
      return [date.clone().startOf('month'), date.clone().endOf('month')]
  }
}

const calculateStartEnd = (view: CalendarView, date: moment.Moment) => {
  const [start, end] = calculateDateRange(view, date)
  start.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
  end.set({ hour: 23, minute: 59, second: 59, millisecond: 999 })
  return [start, end]
}

const SchedulingTab = (props: FacilityProps) => {
  const { facility } = props
  const [date, setDate] = useState(moment())
  const [view, setView] = useState<CalendarView>('week')
  const [createOpen, setCreateOpen] = useState(false)
  const [createStart, setCreateStart] = useState<moment.Moment | undefined>()
  const [createEnd, setCreateEnd] = useState<moment.Moment | undefined>()
  const [editOpen, setEditOpen] = useState(false)
  const [editAppointment, setEditAppointment] = useState<Appointment | undefined>()

  const [start, end] = calculateStartEnd(view, date)
  const startISO = start.toISOString()
  const endISO = end.toISOString()

  const appointmentsQuery = useQuery(
    ['calendar-appointments', facility.data?.guid, startISO, endISO],
    () =>
      skuit.sc.appointments.list({
        params: {
          page_size: 500,
          facilities: facility.data?.guid,
          start_before: endISO,
          start_after: startISO,
        },
      }),
    { enabled: !!facility.data?.guid }
  )
  const facilitySchedule = useQuery(['facility-schedule', facility.data?.guid], () =>
    skuit.sc.facilities.schedule.list(facility.data?.guid || '')
  )

  const handleSlotClick = (start: moment.Moment) => {
    setCreateStart(start)
    setCreateEnd(start.clone().add(1, 'hour'))
    setCreateOpen(true)
  }

  const handleCreateClose = () => {
    setCreateOpen(false)
    setCreateStart(undefined)
    setCreateEnd(undefined)
  }

  return (
    <DetailTab>
      {createOpen && (
        <AppointmentDialog
          open={createOpen}
          handleClose={handleCreateClose}
          facility={facility.data}
          initialStart={createStart}
          initialEnd={createEnd}
          onSave={() => appointmentsQuery.refetch()}
          hoursOfOperation={facilitySchedule.data?.hoursOfOperation}
        />
      )}
      {editOpen && editAppointment && (
        <AppointmentDialog
          open={editOpen}
          handleClose={() => {
            setEditOpen(false)
            setEditAppointment(undefined)
          }}
          update
          appointment={editAppointment}
          facility={facility.data}
          onSave={() => appointmentsQuery.refetch()}
          hoursOfOperation={facilitySchedule.data?.hoursOfOperation}
        />
      )}
      <Grid container justify="space-between" alignItems="center" style={{ marginBottom: '1rem' }}>
        <Grid item>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <IconButton
                size="small"
                onClick={() =>
                  view === 'day'
                    ? setDate(date.clone().subtract(1, 'day'))
                    : setDate(date.clone().subtract(1, 'week'))
                }
              >
                <NavigateBeforeIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <Typography variant="h6">
                {view === 'day'
                  ? date.format('dddd, MMMM D, YYYY')
                  : `Week of ${date.clone().weekday(1).format('LL')}`}
              </Typography>
            </Grid>
            <Grid item>
              <IconButton
                size="small"
                onClick={() =>
                  view === 'day'
                    ? setDate(date.clone().add(1, 'day'))
                    : setDate(date.clone().add(1, 'week'))
                }
              >
                <NavigateNextIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <Button size="small" variant="outlined" onClick={() => setDate(moment())}>
                Today
              </Button>
            </Grid>
            <Grid item>
              <IconButton
                size="small"
                onClick={() => appointmentsQuery.refetch()}
                disabled={appointmentsQuery.isFetching}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <ButtonGroup size="small">
                <Button
                  variant={view === 'week' ? 'contained' : 'outlined'}
                  color={view === 'week' ? 'primary' : 'default'}
                  onClick={() => setView('week')}
                >
                  Week
                </Button>
                <Button
                  variant={view === 'day' ? 'contained' : 'outlined'}
                  color={view === 'day' ? 'primary' : 'default'}
                  onClick={() => setView('day')}
                >
                  Day
                </Button>
              </ButtonGroup>
            </Grid>
            <Grid item>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                New Appointment
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Calendar
        date={date}
        view={view}
        appointments={appointmentsQuery.data?.results || []}
        defaultHours={facilitySchedule.data?.hoursOfOperation}
        maxInbound={facilitySchedule.data?.maxInbound || 0}
        maxOutbound={facilitySchedule.data?.maxOutbound || 0}
        loading={appointmentsQuery.isFetching}
        onSlotClick={handleSlotClick}
        onDayClick={(day) => {
          setDate(day)
          setView('day')
        }}
        onEdit={(appt: Appointment) => {
          setEditAppointment(appt)
          setEditOpen(true)
        }}
        onReschedule={(appt: Appointment, newStart: moment.Moment, newEnd: moment.Moment) => {
          skuit.sc.appointments
            .patch(appt.guid, {
              start: newStart.toISOString(),
              end: newEnd.toISOString(),
            })
            .then(() => {
              appointmentsQuery.refetch()
              toast.success(() => (
                <Notification
                  message={`Appointment rescheduled to ${newStart.format(
                    'ddd, MMM D h:mm A'
                  )} – ${newEnd.format('h:mm A')}`}
                />
              ))
            })
            .catch((error) => {
              const data = error?.response?.data
              const detail = data
                ? Object.entries(data)
                    .map(([field, msgs]) => `${field}: ${(msgs as string[]).join(', ')}`)
                    .join(' | ')
                : null
              toast.error(() => (
                <Notification
                  message={`Failed to reschedule appointment${detail ? `: ${detail}` : '.'}`}
                />
              ))
            })
        }}
      />
    </DetailTab>
  )
}

interface PlaceTabProps {
  facility: UseQueryResult<Facility>
}

const PlaceTab = (props: PlaceTabProps) => {
  const { facility } = props
  const { guid } = useParams<PathProps>()

  const { data: hierarchy } = useQuery(['facility-hierarchy'], () =>
    skuit.sc.facilities.hierarchy(guid)
  )

  return (
    <DetailTab>
      <HierarchyDetail
        title="Place Hierarchy"
        facility={facility}
        roots={hierarchy}
        refresh={() => facility.refetch()}
      />
    </DetailTab>
  )
}

const EventsTab = () => {
  const { guid } = useParams<PathProps>()

  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <DetailTab>
      <EventListTable
        paginator={eventsPaginator}
        title="Facility Events"
        fields={['type', 'data', 'by', 'on']}
      />
    </DetailTab>
  )
}

export const FacilityPage = () => {
  const { guid } = useParams<PathProps>()
  const [exportOpen, setExportOpen] = useState(false)

  const facility = useQuery(['facility-details', guid], () => skuit.sc.facilities.retrieve(guid))

  const refresh = () => {
    facility.refetch()
  }
  const exportFilters = { guid: guid }

  return (
    <>
      {exportOpen && (
        <ExportDialog
          name="Inventory Report"
          download={skuit.sc.facilities.exportInventory}
          open={exportOpen}
          fields={['sku', 'num_units', 'place']}
          exportFilters={exportFilters}
          handleClose={() => setExportOpen(false)}
        />
      )}
      <DetailPage
        obj={facility}
        title="Facilities"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'Facilities', url: '/warehouse/facilities' },
        ]}
        detailComponent={<FacilityDetailTable obj={facility} refresh={refresh} />}
        createDialog={{ title: 'New Facility', Dialog: FacilityDialog }}
        tabs={['Config', 'Inventory', 'Scheduling', 'Places', 'Events']}
      >
        <ConfigTab facility={facility} />
        <InventoryTab />
        <SchedulingTab facility={facility} />
        <PlaceTab facility={facility} />
        <EventsTab />
      </DetailPage>
    </>
  )
}
